/**
 * Leaderboard wire layer — single entry point for reading the leaderboard projection from
 * /api/leaderboard. Used by {@code leaderboard.js#renderLeaderboardInto} — every surface the
 * board renders on (game-start, `/leaderboard`, the menu overlay) shares this one read.
 *
 * Lives in its own module so leaderboard.js can reach api.postApi without forming a cycle
 * (leaderboard.js → leaderboard-sync → api.js stays a one-way DAG).
 *
 * Projection-only since slice 3 made serde the leaderboard's authoritative feed: stats are absorbed
 * from the player's serde corpus server-side, inline at the FINISHED transition (server-side corpus
 * sealing, 2026-07-13 — `GameAdvancer#advanceAndFinish`'s corpus seal; no client round-trip at all
 * anymore), so this request no longer ships the localStorage runs — it only identifies the viewer
 * and selects the projection (queryGameMode + sortBy + window). The round-end refresh is the
 * server's job; this is the read a game-start visit performs. The viewer's identity rides the
 * `deviceToken` cookie automatically (server-resolved, DeviceTokenFilter) since slice 2a — no
 * browserId field on this request anymore.
 *
 * Since leaderboard-windowed-views, `window` is a second, orthogonal axis alongside `sortBy`:
 * `null` on a cold start (the server applies its own default rule and echoes what it picked),
 * an explicit `LeaderboardWindow` name on every later, user-driven request.
 *
 * Since hall-of-fame, `month` ("YYYY-MM") rides the same body: null on every live surface, a past
 * calendar month on a hall-of-fame page — where it always travels with `window: "MONTH"`, the pair
 * the server's own validation requires. The response echoes no month; nothing needs one back.
 */
import { postApi } from "../api.js";

export async function fetchLeaderboard(
  gameMode,
  sortBy = "NET",
  window = null,
  month = null,
) {
  const playerName = localStorage.getItem("playerName");
  const res = await postApi("leaderboard", {
    humanName: playerName || null,
    queryGameMode: gameMode,
    sortBy,
    window,
    month,
  });
  if (!res.ok) throw new Error(`status ${res.status}`);
  return res.json();
}
